import { PageSection, PageWrapper } from '@src/components';
import { useLazyGetOrdersQuery } from '@src/services/order/orderApiSlice';
import { Order } from '@src/services/order/orderApiSliceTypes';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageCard } from '../Package';
import DownloadInvoiceButton from './components/DownloadInvoiceButton';
import { PurchaseDetailsModal } from './PurchaseDetails';
import { ReactComponent as StripeIcon } from '@src/assets/icons/Stripe.svg';
import { ReactComponent as BillingIcon } from '@src/assets/images/billing.svg';
import { ReactComponent as BillingDarkIcon } from '@src/assets/images/billing-dark.svg';
import { useSelector } from 'react-redux';
import { RootState } from '@src/store/store';
import { THEME } from '@src/store/layout/layoutSlice';

// should fill based on backend payment texts
export const enum PAYMENT_GATEWAY {
  STRIPE = 'stripe',
}

export const gatewayIcon = {
  [PAYMENT_GATEWAY.STRIPE]: <StripeIcon />,
};

export function Billing() {
  const { t } = useTranslation();
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order>();
  const [getOrders, { data: orders, isLoading }] = useLazyGetOrdersQuery();

  const activeTheme = useSelector((state: RootState) => state.layout.theme);

  useEffect(() => {
    getOrders({});
  }, []);
  const handleSelectPackage = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  return (
    <PageWrapper
      rootClassName='text-wl-on-background'
      title={t('pages.billing.title')}
      loading={isLoading}
    >
      {orders?.items?.length ? (
        orders?.items?.map((item) => (
          <div key={item.id} className='w-full no-underline hover:shadow-none'>
            {item.package && (
              <PageSection className='bg-wl-background p-4 space-y-4 mb-4 rounded-xl'>
                <div className='flex justify-between items-center'>
                  <div className='flex flex-wrap gap-1'>
                    <h2 className=''>{t('pages.billing.invoiceId')}:</h2>

                    <h2>{item.code}</h2>
                  </div>
                  <div className='flex gap-2 lg:gap-4 items-center'>
                    <p className='text-wl-on-surface opacity-50 text-sm'>
                      {dayjs(item.createdAt).format('MM/DD/YYYY')}
                    </p>
                    <DownloadInvoiceButton orderId={item.id} />
                  </div>
                </div>
                <button
                  className='w-full cursor-pointer'
                  onClick={() => handleSelectPackage(item)}
                >
                  <PackageCard
                    className='!bg-wl-surface'
                    item={item.package}
                    showFlag={false}
                    country={item.country}
                  />
                </button>

                <div className='flex w-full justify-between flex-wrap'>
                  <div className='flex flex-wrap gap-1 text-wl-secondary'>
                    <p>ICCID:</p>
                    <p className='text-B-16'>{item.iccid}</p>
                  </div>

                  <div>{gatewayIcon[item.gateway]}</div>
                </div>
              </PageSection>
            )}
          </div>
        ))
      ) : (
        <div className='w-full h-full -mt-10 items-center justify-center flex flex-col gap-4'>
          {activeTheme === THEME.LIGHT ? <BillingIcon /> : <BillingDarkIcon />}

          <h2 className='text-B-20'>{t('pages.billing.noBilling')}</h2>

          <p className='text-M-18'>{t('pages.billing.noDataMessage')}</p>
        </div>
      )}
      {showOrderDetails && selectedOrder && (
        <PurchaseDetailsModal
          onClose={() => setShowOrderDetails(false)}
          orderId={selectedOrder.id}
        />
      )}
    </PageWrapper>
  );
}
